'use client';

import { Suspense, useState, useCallback, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { Spinner } from '@/components/ui';
import { EnvelopeIcon, LockClosedIcon, DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline';

function LoginForm() {
  const searchParams = useSearchParams();
  const initialRole = searchParams.get('role') === 'employer' ? 'employer' : 'worker';
  const redirectUrl = searchParams.get('redirect');

  const [activeTab, setActiveTab] = useState(initialRole);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const toast = useToast();
  const router = useRouter();

  const isWorker = activeTab === 'worker';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('이메일과 비밀번호를 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      const data = await login(email, password);
      toast.success('로그인 성공!');

      const destination = redirectUrl || data.redirectUrl || '/';
      router.push(destination);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = useCallback((role) => {
    setActiveTab(role);
  }, []);

  const focusRingClass = isWorker
    ? 'focus:ring-green-500/30 focus:border-green-500'
    : 'focus:ring-blue-500/30 focus:border-blue-500';

  const signupPath = isWorker ? '/signup/worker' : '/signup/employer';

  return (
    <>
      {/* Tabs */}
      <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
        <button
          type="button"
          onClick={() => handleTabChange('worker')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
            isWorker
              ? 'bg-white text-green-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          구직자
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('employer')}
          className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
            !isWorker
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          기업회원
        </button>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="email"
            placeholder="이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all duration-200 ${focusRingClass}`}
            autoComplete="email"
          />
        </div>

        <div className="relative">
          <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="password"
            placeholder="비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-all duration-200 ${focusRingClass}`}
            autoComplete="current-password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-3.5 px-6 text-white font-semibold rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
            isWorker
              ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30'
              : 'bg-blue-500 hover:bg-blue-600 shadow-lg shadow-blue-500/30'
          }`}
        >
          {loading ? <Spinner size="sm" /> : '로그인'}
        </button>
      </form>

      {/* Kakao Login */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            window.location.href = `/api/auth/kakao?role=${activeTab}`;
          }}
          className="w-full py-3.5 px-6 bg-[#FEE500] hover:bg-[#FDD835] text-[#191919] font-semibold rounded-xl transition-all duration-200 active:scale-[0.98] text-sm flex items-center justify-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M9 0.6C4.03 0.6 0 3.713 0 7.554c0 2.468 1.646 4.636 4.12 5.87-.131.488-.845 3.14-.872 3.345 0 0-.018.145.076.2.095.056.206.025.206.025.271-.038 3.139-2.064 3.636-2.412.27.037.546.056.834.056 4.97 0 9-3.113 9-6.954C18 3.713 13.97 0.6 9 0.6" fill="#191919"/>
          </svg>
          카카오로 시작하기
        </button>
      </div>

      {/* Signup Link */}
      <div className="mt-8 text-center">
        <p className="text-slate-500 text-sm mb-3">
          아직 계정이 없으신가요?
        </p>
        <Link
          href={signupPath}
          className={`inline-flex items-center gap-1 text-sm font-semibold ${
            isWorker ? 'text-green-600' : 'text-blue-600'
          }`}
        >
          {isWorker ? '구직자 회원가입' : '기업회원 가입'}
          →
        </Link>
      </div>
    </>
  );
}

function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // 이미 설치된 경우 또는 이미 닫은 경우
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (sessionStorage.getItem('pwa-dismissed')) return;

    // iOS 감지
    const ua = navigator.userAgent;
    const isIosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);

    if (isIosDevice && isSafari) {
      setIsIos(true);
      setShowBanner(true);
      return;
    }

    // Android/Chrome beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem('pwa-dismissed', '1');
  };

  if (!showBanner) return null;

  return (
    <div className="mx-6 mb-4">
      <div className="relative bg-slate-800 text-white rounded-2xl p-4 shadow-lg">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 hover:bg-slate-700 rounded-lg"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 bg-slate-700 rounded-xl flex items-center justify-center flex-shrink-0">
            <DevicePhoneMobileIcon className="w-5 h-5 text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">앱으로 더 편리하게</p>
            {isIos ? (
              <p className="text-xs text-slate-300 mt-1">
                Safari 하단의 <span className="inline-block px-1 bg-slate-600 rounded text-[10px]">공유</span> 버튼 → <strong>홈 화면에 추가</strong>를 눌러주세요
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-300 mt-1">
                  홈 화면에 추가하고 앱처럼 사용하세요
                </p>
                <button
                  onClick={handleInstall}
                  className="mt-2 px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold rounded-lg transition-colors"
                >
                  설치하기
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="p-6">
        <Link href="/" className="text-2xl font-bold text-slate-800">
          다새로
        </Link>
      </div>

      {/* PWA Install Banner */}
      <PwaInstallBanner />

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">로그인</h1>
            <p className="text-slate-500 text-sm">계정에 로그인하세요</p>
          </div>

          <Suspense fallback={<div className="h-48 flex items-center justify-center"><Spinner /></div>}>
            <LoginForm />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
